/**
 * Metadatos por ruta: fuente unica para title, description, breadcrumb y datos
 * estructurados (JSON-LD).
 *
 * Por que existe: 6 paginas (/contributors, /dashboard, /database, /garden,
 * /releases, /guides) no tenian <Head> propio, asi que heredaban el title
 * y la description del sitio y quedaban como duplicados exactos de la home. Titulos
 * duplicados son una de las señales que Google lee como "contenido de bajo valor".
 *
 * Las paginas que ya definen su propio <Head> siguen ganando: next/head resuelve
 * el duplicado quedandose con el ultimo, y el <Head> de la pagina se monta despues
 * del de _app. Este mapa actua de piso, no de techo.
 *
 * Las descripciones salen de lo que ya describe menuList en menu.js — texto escrito
 * por el equipo, no inventado.
 */

export const SITIO = {
  nombre: 'WAO Tools',
  url: 'https://waotools.com',
  logo: 'https://waotools.com/images/icon-512.png',
  /*
    Tarjeta social. Va aparte del logo porque og:image y twitter:card
    summary_large_image piden 1200x630 (1.91:1); el logo cuadrado lo recortaban.
  */
  tarjeta: 'https://waotools.com/images/og-waotools.png',
  tarjetaAncho: 1200,
  tarjetaAlto: 630,
  twitter: '@waotools',
  autor: 'oNare',
  descripcion:
    'Free tools, calculators, and guides for War and Order players. Plan troops, artifacts, buildings, stamina, and more — all in one place.'
}

/** `tipo` decide que JSON-LD se emite: 'herramienta' -> SoftwareApplication, 'info' -> WebPage. */
export const RUTAS_SEO = {
  '/': {
    titulo: 'WAO Tools — War and Order Calculators, Planners & Guides',
    descripcion: SITIO.descripcion,
    tipo: 'inicio',
    breadcrumb: []
  },
  '/dashboard': {
    titulo: "War and Order Castle Dashboard | WAO Tools",
    descripcion: "Your War and Order castle at a glance: building levels, research state and growth, all on one page.",
    tipo: 'herramienta',
    breadcrumb: ['Dashboard']
  },
  '/database': {
    titulo: "War and Order Database — Obsidian & VIP Tables",
    descripcion: "Reference tables for War and Order: Obsidian enhancement levels, VIP and SVIP benefits, and more lookup tables in one searchable place.",
    tipo: 'herramienta',
    breadcrumb: ['Database']
  },
  '/garden': {
    titulo: "War and Order Garden Decorations & Sets | WAO Tools",
    descripcion: "Garden decoration stats, complete sets and statues for War and Order. Compare bonuses and plan which set to finish first.",
    tipo: 'herramienta',
    breadcrumb: ['Garden']
  },
  '/contributors': {
    titulo: 'Contributors — The People Behind WAO Tools',
    descripcion:
      'Meet the players who build and maintain WAO Tools, and see how to support the project. WAO Tools is an independent fan-made toolkit.',
    tipo: 'info',
    breadcrumb: ['Contributors']
  },
  '/releases': {
    titulo: 'Release Notes — WAO Tools Changelog',
    descripcion:
      'Changelog and latest feature updates for WAO Tools. See what tools were added, what data was corrected, and what changed in each release.',
    tipo: 'info',
    breadcrumb: ['Release Notes']
  },

  // Las de abajo tenian su propio <Head> con title, description y canonical
  // hardcodeados. Se movieron aca para que haya una sola fuente: dos <link
  // rel='canonical'> en la misma pagina hacen que Google ignore los dos.
  '/about': {
    titulo: 'About WAO Tools — The Story Behind the Site',
    descripcion:
      'WAO Tools is an independent fan-made toolkit for War and Order players. Learn about the creator oNare, why WAO Tools was built, and what makes it different.',
    tipo: 'info',
    breadcrumb: ['About']
  },
  '/artifacts': {
    titulo: 'War and Order Artifacts Planner — WAO Tools',
    descripcion:
      'Configure War and Order artifact setups for each force. Compare stat bonuses and plan loadouts before you spend materials in-game.',
    tipo: 'herramienta',
    breadcrumb: ['Artifacts']
  },
  '/azurite': {
    titulo: 'War and Order Azurite Calculator — WAO Tools',
    descripcion:
      'Compute exact resource costs for Azurite upgrades in War and Order. Enter your current and target level to plan spending on this premium combat stat resource.',
    tipo: 'herramienta',
    breadcrumb: ['Azurite Calculator']
  },
  '/beast': {
    titulo: 'War and Order Beast Skills Planner — WAO Tools',
    descripcion:
      'Configure your War and Order beast skills, abilities, and formation positioning. Save setups in your browser for quick switching.',
    tipo: 'herramienta',
    breadcrumb: ['Beast']
  },
  '/building': {
    titulo: 'War and Order Buildings List — Costs by Level | WAO Tools',
    descripcion:
      'Complete War and Order buildings reference with Azurite, Food, Wood, Stone and Iron costs at every upgrade level. Filter and sort by type or name.',
    tipo: 'herramienta',
    breadcrumb: ['Buildings List']
  },
  '/college': {
    titulo: 'War and Order High College Simulator — WAO Tools',
    descripcion:
      'Simulate War and Order High College research upgrades. Configure research paths and see resource requirements to unlock powerful bonuses faster.',
    tipo: 'herramienta',
    breadcrumb: ['High College']
  },
  '/colossus': {
    titulo: 'War and Order Colossus Simulator — WAO Tools',
    descripcion:
      'Simulate War and Order Colossus builds. Set crystal limits, troop positions and ability slots to see the cost of each path before committing.',
    tipo: 'herramienta',
    breadcrumb: ['Colossus']
  },
  '/contact': {
    titulo: 'Contact WAO Tools — Report a Bug or Suggest a Tool',
    descripcion:
      'Get in touch with the WAO Tools team. Report bugs, suggest new tools, or ask questions about War and Order calculators.',
    tipo: 'info',
    breadcrumb: ['Contact']
  },
  '/disclaimer': {
    titulo: 'Disclaimer — WAO Tools, an Unofficial War and Order Fan Site',
    descripcion:
      'WAO Tools is an independent, fan-made project and is not affiliated with, endorsed by, or connected to the publishers of War and Order.',
    tipo: 'info',
    breadcrumb: ['Disclaimer']
  },
  '/equipment': {
    titulo: 'War and Order Equipment & Enhancements — WAO Tools',
    descripcion:
      'Plan War and Order gear upgrades with the Equipment and Enhancements tool. Configure equipment pieces, enhancement levels, and compare stat requirements.',
    tipo: 'herramienta',
    breadcrumb: ['Equipment']
  },
  '/guides': {
    titulo: 'War and Order Guides — Costs, Tricks & Tables',
    descripcion:
      'Free War and Order guides covering beginner strategy, troop management, stamina, heroes, research, and more. Written by players, for players.',
    tipo: 'info',
    breadcrumb: ['Guides']
  },
  '/jewels': {
    titulo: 'War and Order Jewel Upgrade Calculator — WAO Tools',
    descripcion:
      'Calculate War and Order jewel upgrade costs. Enter your current and target jewel levels to see required materials and plan your investments.',
    tipo: 'herramienta',
    breadcrumb: ['Jewels']
  },
  '/minitools': {
    titulo: "War and Order Mini Tools — Angel Dust Calculator",
    descripcion: "Quick calculators for War and Order: Angel capacity estimator, stamina helpers and other small in-game utilities.",
    tipo: 'herramienta',
    breadcrumb: ['Mini Tools']
  },
  '/mystic': {
    titulo: 'War and Order Mystic College Simulator — WAO Tools',
    descripcion:
      'Plan War and Order Mystic College ability upgrades. Select abilities to unlock and see total resource costs to optimize your mystic progression.',
    tipo: 'herramienta',
    breadcrumb: ['Mystic College']
  },
  '/packs': {
    titulo: 'War and Order Pack Tracker — Compare Pack Value | WAO Tools',
    descripcion:
      'Track your War and Order pack purchases and spending. Log packs by type, calculate total gems and monthly costs, and monitor your spending over time.',
    tipo: 'herramienta',
    breadcrumb: ['Packs']
  },
  '/planner': {
    titulo: 'War and Order Building & Tech Planner — WAO Tools',
    descripcion:
      'Plan War and Order building upgrades and research in one queue. See which buildings and techs to prioritise, with Azurite costs included.',
    tipo: 'herramienta',
    breadcrumb: ['Planner']
  },
  '/privacy': {
    titulo: 'Privacy Policy — WAO Tools Data and Cookie Practices',
    descripcion:
      'WAO Tools Privacy Policy. Learn how we collect, use, and protect your data, including information about cookies and Google AdSense advertising.',
    tipo: 'info',
    breadcrumb: ['Privacy Policy']
  },
  '/realms': {
    titulo: "War and Order Realms — Merge History | WAO Tools",
    descripcion: "Realm reference for War and Order: merge history, server details, event schedules and notes on the realms you play.",
    tipo: 'herramienta',
    breadcrumb: ['Realms']
  },
  '/shields': {
    titulo: "War and Order Shield Timer & Tracker | WAO Tools",
    descripcion: "Track War and Order shield timings across realms: server times, shield durations and multi-realm schedules saved in your browser.",
    tipo: 'herramienta',
    breadcrumb: ['Shields']
  },
  '/speeds': {
    titulo: 'War and Order Recruitment Speed Calculator — WAO Tools',
    descripcion:
      'Calculate War and Order troop recruitment times from your speed buffs. Enter training bonuses and troop type for an accurate time estimate.',
    tipo: 'herramienta',
    breadcrumb: ['Recruitment Speeds']
  },
  '/terms': {
    titulo: 'Terms of Service — WAO Tools Usage Conditions',
    descripcion:
      'Terms of Service for WAO Tools. Read our terms covering permitted use, intellectual property, disclaimers, and limitations of liability.',
    tipo: 'info',
    breadcrumb: ['Terms of Service']
  },
  '/totem': {
    titulo: 'War and Order Totems — Stat Bonuses and Stones | WAO Tools',
    descripcion:
      'Configure War and Order totem levels to see stat bonuses for your troops. Save and compare different totem upgrade paths to find the best strategy.',
    tipo: 'herramienta',
    breadcrumb: ['Totems']
  },
  '/troops': {
    titulo: "War and Order Troop Setups & Formations | WAO Tools",
    descripcion: "Build and save War and Order troop formations. Even and Odd tiers, Angels, and layouts for Anubis, Elite War, SOS and more.",
    tipo: 'herramienta',
    breadcrumb: ['Troops Setup']
  }
}

/**
 * Rutas que son herramientas interactivas, para el JSON-LD de SoftwareApplication.
 * Se listan aparte porque casi todas ya traen su propio <Head> y solo les falta
 * el dato estructurado.
 */
/**
 * Paginas de error. Necesitan title propio (heredaban el de la home) y sobre todo
 * noindex: sin eso, Google puede indexar /404/ como una URL real y ademas recibia
 * un canonical apuntando a si misma, que es afirmar que es contenido legitimo.
 */
export const RUTAS_ERROR = {
  '/404': {
    titulo: 'Page Not Found — WAO Tools',
    descripcion: 'That page does not exist. Browse the War and Order tools and guides from the menu.'
  },
  '/401': { titulo: 'Not Authorized — WAO Tools', descripcion: 'You do not have access to this page.' },
  '/500': {
    titulo: 'Something Went Wrong — WAO Tools',
    descripcion: 'An unexpected error occurred. Try reloading the page.'
  }
}

export const esRutaError = clave => Object.prototype.hasOwnProperty.call(RUTAS_ERROR, clave)

export const RUTAS_HERRAMIENTA = [
  '/dashboard',
  '/planner',
  '/troops',
  '/azurite',
  '/building',
  '/shields',
  '/speeds',
  '/beast',
  '/totem',
  '/colossus',
  '/garden',
  '/mystic',
  '/college',
  '/minitools',
  '/database',
  '/packs',
  '/artifacts',
  '/equipment',
  '/jewels',
  '/realms'
]

/** Migas por ruta para las herramientas que no estan en RUTAS_SEO. */
export const MIGAS = {
  '/planner': ['Planner'],
  '/troops': ['Troops Setup'],
  '/azurite': ['Azurite Calculator'],
  '/building': ['Buildings List'],
  '/shields': ['Shields'],
  '/speeds': ['Recruitment Speeds'],
  '/beast': ['Beast'],
  '/totem': ['Totems'],
  '/colossus': ['Colossus'],
  '/mystic': ['Mystic College'],
  '/college': ['High College'],
  '/minitools': ['Mini Tools'],
  '/packs': ['Packs'],
  '/artifacts': ['Artifacts'],
  '/equipment': ['Equipment'],
  '/jewels': ['Jewels'],
  '/realms': ['Realms'],
  '/guides': ['Guides'],
  '/about': ['About'],
  '/contact': ['Contact'],
  '/privacy': ['Privacy Policy'],
  '/terms': ['Terms of Service'],
  '/disclaimer': ['Disclaimer']
}

/**
 * Rutas que arman su cabecera por su cuenta porque los metadatos dependen del
 * contenido, no de la ruta. /guides/[slug] ya emite og:*, article:*, canonical y
 * JSON-LD de Article a partir del frontmatter de cada guia.
 */
export const RUTAS_CON_HEAD_PROPIO = [/^\/guides\/[^/]+$/]

export const tieneHeadPropio = clave => RUTAS_CON_HEAD_PROPIO.some(r => r.test(clave))

/** Normaliza asPath a una clave del mapa: sin query, sin hash, sin barra final. */
export const claveDeRuta = asPath => {
  const limpio = String(asPath || '/')
    .split('?')[0]
    .split('#')[0]
  if (limpio === '/' || limpio === '') return '/'

  return limpio.replace(/\/+$/, '')
}

/** URL canonica absoluta, respetando trailingSlash: true de next.config.js. */
export const canonicaDeRuta = asPath => {
  const clave = claveDeRuta(asPath)

  return clave === '/' ? `${SITIO.url}/` : `${SITIO.url}${clave}/`
}
